import React, { memo } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import LogoLoop from './LogoLoop'

/**
 * Client logo marquee.
 *
 * The logo files are 1668x834 JPEGs of roughly 900KB each, and they render into
 * a slot that howwework.scss pins to exactly 220x80. Imported directly with
 * `import x from '../images/clients/X.jpg'` they went through webpack, not
 * sharp, so the browser downloaded 22 full-size originals — 18.9MB — to paint
 * 22 postage stamps. That single fact was the whole of Lighthouse's 88750ms
 * "properly size images" opportunity on mobile.
 *
 * Routing them through childImageSharp instead gives correctly sized
 * renditions, a real srcSet so the browser picks by DPR, and WebP. LogoLoop
 * already renders src/srcSet/sizes/width/height and sets loading="lazy" and
 * decoding="async" on every image, so nothing there needed changing.
 *
 * srcWebp/srcSetWebp are used directly rather than through a <picture> element
 * because LogoLoop renders a bare <img>. WebP is universally supported by every
 * browser this site targets, so the format negotiation a <picture> would buy is
 * not worth restructuring the marquee for.
 *
 * The list below is ordered; `name` is the source filename without extension
 * and is what joins a row to its processed image. Muscat_Media_Group appears
 * twice on purpose — it did before, and the marquee reads better for it.
 */
const LOGOS = [
  { name: 'Centric_Events_Rentals', alt: 'Centric Events Rentals' },
  { name: 'MIA_group_of_compnies', alt: 'MIA Group of Companies' },
  { name: 'Cakes_Bakes', alt: 'Cakes & Bakes' },
  { name: 'Ali_zaid_Brothers', alt: 'Ali Zaid Brothers' },
  { name: 'Alhamd_corporation', alt: 'Alhamd Corporation' },
  { name: 'averti_infotech', alt: 'Averti Infotech' },
  { name: 'AXR_solution', alt: 'AXR Solution' },
  { name: 'Borjan', alt: 'Borjan' },
  { name: 'Ecozohm', alt: 'Ecozohm' },
  { name: 'OIL_Northern', alt: 'SOI Northern' },
  { name: 'evolved_office', alt: 'Evolved Office' },
  { name: 'BIG_jerry_fencing', alt: "Big Jerry's Fencing" },
  { name: 'Muscat_Media_Group', alt: 'Muscat Media Group' },
  { name: 'Hush_Puppies', alt: 'Hush Puppies' },
  { name: 'Zie_Pie', alt: 'Zie Pie' },
  { name: 'orr_protection', alt: 'ORR Protection' },
  { name: 'Oman_data_park', alt: 'Oman Data Park' },
  { name: 'Net_Atlantic', alt: 'Net Atlantic' },
  { name: 'Orange_Line', alt: 'Orange Line' },
  { name: 'rene_well_water', alt: 'Renewell Water' },
  { name: 'Muscat_Media_Group', alt: 'Muscat Media Group' },
  { name: 'Nutureful', alt: 'Nurtureful' },
]

const HappyClients = () => {
  // maxWidth 220 matches the rendered slot. sharp emits the standard fluid
  // ladder from that (0.25x through 3x), so a 3dpr phone still gets a sharp
  // 660px rendition and a 1dpr laptop gets 220px — instead of 1668px for both.
  const data = useStaticQuery(graphql`
    query ClientLogos {
      allFile(
        filter: {
          relativeDirectory: { eq: "clients" }
          extension: { in: ["jpg", "jpeg", "png"] }
        }
      ) {
        nodes {
          name
          childImageSharp {
            fluid(maxWidth: 220, quality: 80) {
              src
              srcSet
              sizes
              srcWebp
              srcSetWebp
            }
          }
        }
      }
    }
  `)

  const byName = {}
  data.allFile.nodes.forEach(n => {
    if (n.childImageSharp) byName[n.name] = n.childImageSharp.fluid
  })

  const logos = LOGOS.map(({ name, alt }) => {
    const f = byName[name]
    if (!f) return null
    return {
      src: f.srcWebp || f.src,
      srcSet: f.srcSetWebp || f.srcSet,
      sizes: f.sizes,
      width: 220,
      height: 80,
      alt,
    }
  }).filter(Boolean)

  return (
    <section className="tc-section">
      {/* "Worldwide" stays gone — it was a scale claim, and the retired hero
          band's "4 Continents Served" was dropped for the same reason (see the
          NOT INCLUDED note in HomePageIntro.js). No scale word replaces it.

          COUNT CONFIRMED AT 25 BY THE BRIEF. Recorded because the page's own
          evidence does not reach it: LOGOS below has 22 slots and Muscat Media
          Group appears twice on purpose, so the marquee renders 21 distinct
          clients. "More than 25" is therefore a claim about the client base,
          not a count of what is on screen. If it should track the marquee
          instead, the figure is 21 — or 22 once Times_of_Oman, which sits in
          src/images/clients/ but not in LOGOS, is added.

          The split matches the previous structure exactly: "Trusted by" at full
          size, the rest in the accent span, which clients.scss sets at under
          half the heading size. */}
      <h2 className="tc-heading">
        Trusted by{" "}
        <span className="tc-heading__accent">more than 25 organizations</span>
      </h2>
      <p className="tc-sub">
        Enterprises across the Middle East, Europe, and North America rely on Visiomate
        to build, optimize, and scale their technology.
      </p>
      <div style={{ height: '140px', position: 'relative', overflow: 'hidden' }}>
        <LogoLoop
          logos={logos}
          speed={100}
          direction="left"
          logoHeight={80}
          gap={0}
          hoverSpeed={0}
          scaleOnHover
          fadeOut
          fadeOutColor="#ffffff"
          ariaLabel="Happy clients"
          style={{ '--logoloop-logoWidth': '220px' }}
        />
      </div>
    </section>
  )
}

export default memo(HappyClients)
